/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context/types';
import { PreviewCard } from '@atlassian/conversation-assistant-ui-components/ui/PreviewCard';

import {
	type UploadedFile,
	useAllAttachmentsStore,
} from '../../../common/controllers/attachtment-store';

import { FileCard } from './file-card';

const styles = cssMap({
	wrapper: {
		overflowX: 'auto',
		whiteSpace: 'nowrap',
		maxWidth: '100%',
		scrollBehavior: 'smooth',
		paddingBlock: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
});

const useAutoScrollOnDataChange = (data: any[]) => {
	const prevDataLength = useRef(data.length);
	const [isAutoScroll, setIsAutoScroll] = useState(false);

	const dataLength = data.length;

	useEffect(() => {
		if (prevDataLength.current < dataLength) {
			setIsAutoScroll(true);
		} else {
			setIsAutoScroll(false);
		}
		prevDataLength.current = dataLength;
	}, [dataLength]);

	return isAutoScroll;
};

export const InputContextListDisplay = ({
	attachtmentsStoreKey,
}: {
	attachtmentsStoreKey: string;
}): JSX.Element | null => {
	const [attachmentsState, { removeExternalContext, removeFile }] =
		useAllAttachmentsStore(attachtmentsStoreKey);
	const isAutoScroll = useAutoScrollOnDataChange(attachmentsState);

	if (attachmentsState.length === 0) {
		return null;
	}

	return (
		<Inline space="space.150" xcss={styles.wrapper} shouldWrap={false}>
			{attachmentsState.map((context, index) => {
				const isLast = index === attachmentsState.length - 1;
				switch (context.type) {
					case 'file':
						const file = context.data as UploadedFile;
						return (
							<FileCard
								key={file.name}
								fileName={file.name}
								onRemove={() => removeFile(attachtmentsStoreKey, file.name)}
								isLoading={file.isLoading}
								error={file.error}
								autoScroll={isAutoScroll && Boolean(file.error || isLast)}
								previewBlobUrl={file.previewBlobUrl}
								isPreviewLoading={file.isPreviewLoading}
								fileObject={file.fileObject}
							/>
						);
					case 'external':
						const promptInputContext = context.data as PromptInputContext;
						const externalContext = promptInputContext.representation;
						return (
							<PreviewCard
								key={promptInputContext.key}
								label={externalContext.label}
								subLabel={externalContext.description}
								onRemove={() => removeExternalContext(attachtmentsStoreKey, promptInputContext.key)}
								autoScroll={isAutoScroll && Boolean(isLast)}
								cardIcon={externalContext.image}
							/>
						);
					default:
						return null;
				}
			})}
		</Inline>
	);
};
